import axios from "axios";
import { toastError } from "@/utils/toast";
import { useUserStore } from "@/store/useUserStore";
import { useChatSessionStore } from "@/store/useChatSessionStore";

const baseURL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

// ─── 错误 toast 去重 ───────────────────────────────
let lastToastMsg = "";
let lastToastTime = 0;
const TOAST_DEBOUNCE_MS = 800;

function dedupToastError(message: string) {
  const now = Date.now();
  if (message === lastToastMsg && now - lastToastTime < TOAST_DEBOUNCE_MS)
    return;
  lastToastMsg = message;
  lastToastTime = now;
  toastError(message);
}

export const http = axios.create({
  baseURL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 请求拦截器
http.interceptors.request.use(
  (config) => {
    // 支持通过 config._skipAuth 跳过 token 注入
    if (!config._skipAuth) {
      let { token, tokenType } = useUserStore.getState();

      // 兜底：Zustand persist 可能还未从 localStorage 异步恢复完毕，
      // 此时 getState() 返回的是初始值(null)，需要直接读 localStorage 补齐
      if (!token) {
        try {
          const raw = localStorage.getItem("user-storage");
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.state?.token || parsed?.token) {
              token = parsed.state?.token ?? parsed.token;
              tokenType = parsed.state?.tokenType ?? parsed.tokenType ?? "Bearer";
            }
          }
        } catch {
          /* ignore */
        }
      }

      if (token) {
        config.headers.Authorization = `${tokenType} ${token}`;
      }
    }

    // 设置聊天会话 ID
    const { session_id } = useChatSessionStore.getState();
    if (session_id) {
      config.headers["Chat-Session"] = session_id;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
http.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 统一错误提示
    let message = "网络异常，请稍后重试";
    const skipAuthRedirect = Boolean(
      (error.config as { _skipAuthRedirect?: boolean } | undefined)
        ?._skipAuthRedirect,
    );

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      // 401 / token失效：优先处理，必须跳转授权
      if (status === 401) {
        if (skipAuthRedirect) {
          console.error("[Axios Error]", error);
          return Promise.reject(error);
        }
        // ★ 开发环境：只打日志不跳转
        if (import.meta.env.DEV) {
          console.warn("[peiwantHttp] HTTP 401 → 开发环境跳过跳转");
          return Promise.reject(error);
        }
        message = data?.message || "登录已过期，请重新登录";
        dedupToastError(message);
        setTimeout(() => {
          useUserStore.getState().logout();
          const currentPath = window.location.pathname + window.location.search;
          if (
            !currentPath.startsWith("/wx-auth") &&
            !currentPath.startsWith("/wx-callback")
          ) {
            window.location.href = `/wx-auth?redirect=${encodeURIComponent(currentPath)}`;
          }
        }, 1000);
      } else if (data?.message) {
        message = data.message;
      } else {
        switch (status) {
          case 400:
            message = "请求参数错误";
            break;
          case 403:
            message = "没有权限访问";
            break;
          case 404:
            message = "请求的资源不存在";
            break;
          case 422:
            message = data?.message || "数据验证失败";
            break;
          case 429:
            message = "操作过于频繁，请稍后再试";
            break;
          case 500:
            message = "服务器内部错误";
            break;
          case 502:
            message = "服务网关错误，请稍后重试";
            break;
          case 503:
            message = "服务暂不可用，请稍后重试";
            break;
        }
      }
    } else if (error.message?.includes("timeout")) {
      message = "请求超时，请检查网络后重试";
    } else if (error.message?.includes("Network Error") || !navigator.onLine) {
      message = "网络连接失败，请检查网络设置";
    }

      console.error("[Axios Error]", error);
      dedupToastError(message);

      return Promise.reject(error);
    },
);

// ─── SSE 专用实例（路径不带 /api 前缀，认证用 Chat-Token / Chat-Session）──
//
// baseURL 策略：
//   - 开发环境：""（空字符串），/sse/* 由 vite dev server proxy 转发
//   - 生产环境：从 VITE_API_BASE_URL 提取基础域名（去掉 /api 后缀），
//               如 "https://testmex.banly.com.cn/api" → "https://testmex.banly.com.cn"
function getSseBaseURL(): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL || "";
  if (!apiBase || apiBase.startsWith("/")) {
    // 开发环境相对路径 → 由 vite proxy 处理
    return "";
  }
  // 生产环境完整 URL → 去掉 /api 后缀得到 SSE 基础域名
  return apiBase.replace(/\/api\/?$/, "");
}

export const sseHttp = axios.create({
  baseURL: getSseBaseURL(),
  timeout: 30000,
  headers: {},
});

// sseHttp 请求拦截器：注入 Chat-Token + Chat-Session（不走 Authorization Bearer）
sseHttp.interceptors.request.use(
  (config) => {
    let { token } = useUserStore.getState();

    // ★ 兜底：Zustand persist 可能还未从 localStorage 异步恢复完毕，
    //   此时 getState() 返回的是初始值(null)，需要直接读 localStorage 补齐
    if (!token) {
      try {
        const raw = localStorage.getItem("user-storage");
        if (raw) {
          const parsed = JSON.parse(raw);
          token = parsed?.state?.token ?? parsed?.token ?? null;
        }
      } catch {
        /* ignore */
      }
    }

    if (token) {
      config.headers["Chat-Token"] = token;
    }

    let { session_id } = useChatSessionStore.getState();
    // ★ 同样对 session_id 做 localStorage 兜底
    if (!session_id) {
      try {
        const raw = localStorage.getItem("chat-session-storage");
        if (raw) {
          const parsed = JSON.parse(raw);
          session_id = parsed?.state?.session_id ?? parsed?.session_id ?? null;
        }
      } catch {
        /* ignore */
      }
    }

    if (session_id) {
      config.headers["Chat-Session"] = session_id;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// sseHttp 响应拦截器：只做基本错误处理
sseHttp.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("[sseHttp Error]", error.config?.url, error);
    return Promise.reject(error);
  },
);

// ─── peiwant 实例（BLW旧项目域名：peiwan.banly.com.cn）────────
// 开发/生产环境：均直接请求 https://peiwan.banly.com.cn（BLW 已配置 CORS）
//
// 注意：peiwantHttp 有独立的认证体系，不与 http(mex-claw新系统)共享 token。
// BLW 旧系统使用 user_token 认证，格式不同于 Bearer token。
const peiwantBaseURL = "https://peiwan.banly.com.cn";

export const peiwantHttp = axios.create({
  baseURL: peiwantBaseURL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    // BLW 系统使用 Form-type 而非 platform（避免 CORS 拦截）
    "Form-type": "h5",
  },
});

// peiwantHttp 独立的请求拦截器
// BLW 旧系统认证头格式：Authorization: {{user_token}} （无 Bearer 前缀）
// Token 优先级：supplier_token > user-storage 主系统 token（自动同步）
peiwantHttp.interceptors.request.use(
  (config) => {
    // 支持通过 config._skipAuth 跳过 token 注入（公开接口用）
    if (!(config as any)._skipAuth) {
      let blwToken: string | null = null;

      try {
        // 1. 优先读 BLW 专用 token
        blwToken = localStorage.getItem("supplier_token");

        // 2. fallback：如果 supplier_token 为空，尝试从主系统 user-storage 同步
        if (!blwToken) {
          const rawUserStorage = localStorage.getItem("user-storage");
          if (rawUserStorage) {
            const parsed = JSON.parse(rawUserStorage);
            blwToken = parsed?.state?.token ?? parsed?.token ?? null;
            if (blwToken) {
              console.log("[peiwantHttp] 从 user-storage 同步 token:", blwToken.slice(0, 12) + "...");
            }
          }
        }
      } catch {
        /* ignore */
      }

      // BLW 格式：Authorization: {原始token}  （不带 Bearer 前缀）
      if (blwToken) {
        config.headers.Authorization = blwToken;
        console.log("[peiwantHttp] 注入 Authorization:", blwToken.slice(0, 12) + "...");
      } else {
        console.warn("[peiwantHttp] 未找到任何可用 token，请求可能返回未登录");
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

peiwantHttp.interceptors.response.use(
  (response) => {
    const data = response.data;

    // HTTP 204 No Content: 服务端返回空体 → 统一转为成功对象
    if (response.status === 204 || data === "") {
      response.data = { status: 204, msg: "success" };
      return response;
    }

    // 检测 BLW 业务层 401（HTTP 200 但 body.status === 401 表示未登录）
    if (data && data.status === 401) {
      const msg = data.msg || "未登录，请先登录";
      console.warn("[peiwantHttp] 业务层 401:", msg, import.meta.env.DEV ? "→ 开发环境跳过跳转" : "→ 跳转登录");

      // ★ 开发环境：只打印日志不跳转，避免写死 token 失效时反复弹登录
      if (import.meta.env.DEV) {
        return Promise.reject({
          response,
          isBlwAuthError: true,
          message: msg,
        });
      }

      dedupToastError(msg);

      // 延迟跳转，让用户看到提示
      setTimeout(() => {
        const currentPath = window.location.pathname + window.location.search;
        if (
          !currentPath.startsWith("/wx-auth") &&
          !currentPath.startsWith("/wx-callback")
        ) {
          window.location.href = `/wx-auth?redirect=${encodeURIComponent(currentPath)}`;
        }
      }, 1200);

      // 抛出错误阻止后续业务逻辑
      return Promise.reject({
        response,
        isBlwAuthError: true,
        message: msg,
      });
    }

    return response;
  },
  (error) => {
    // HTTP 层面错误（网络超时、500 等）
    const data = error.response?.data;
    let message = "请求失败";

    // 尝试从 BLW 响应中提取错误信息
    if (data) {
      message = data?.msg || data?.message || String(data?.status || data?.code || "");
      if (!message || message === "" || /^\d+$/.test(message)) {
        message = "服务异常，请稍后重试";
      }
    }

    console.error("[peiwantHttp Error]", error.config?.url, error);

    // ★ 开发环境 401：不弹 toast 避免干扰调试
    if (import.meta.env.DEV && error.response?.status === 401) {
      return Promise.reject(error);
    }

    dedupToastError(message);
  },
);
